import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Icon } from '@iconify/react';
import { useAuth } from '@/contexts/AuthContext';
import { useLoginDrawer } from '@/contexts/LoginDrawerContext';

import { impactLight } from '@/utils/nativeHaptics';
import { clientPaths, isClientHome, shouldHideClientBottomNav } from '@/utils/clientPaths';

export interface NavItemConfig {
  label: string;
  icon: string;
  activeIcon: string;
  path: string;
  /** Guest: open login drawer instead of navigate */
  guestDrawer?: boolean;
  /** Guest: navigate to full login page */
  guestLoginPage?: boolean;
}

export interface ClientMobileBottomNavProps {
  items?: NavItemConfig[];
}

const DEFAULT_CLIENT_NAV_ITEMS: NavItemConfig[] = [
  {
    label: 'Home',
    icon: 'ph:house',
    activeIcon: 'ph:house-fill',
    path: clientPaths.home,
  },
  {
    label: 'Event',
    icon: 'ph:calendar-dots',
    activeIcon: 'ph:calendar-dots-fill',
    path: clientPaths.event.list,
    guestDrawer: true,
  },
  {
    label: 'Discovery',
    icon: 'ph:compass',
    activeIcon: 'ph:compass-fill',
    path: clientPaths.discovery.root,
  },
  {
    label: 'Booking',
    icon: 'ph:ticket',
    activeIcon: 'ph:ticket-fill',
    path: clientPaths.booking.list,
    guestDrawer: true,
  },
  {
    label: 'Profil',
    icon: 'ph:user-circle',
    activeIcon: 'ph:user-circle-fill',
    path: clientPaths.profile.settings,
    guestLoginPage: true,
  },
];

export const ClientMobileBottomNav: React.FC<ClientMobileBottomNavProps> = ({
  items = DEFAULT_CLIENT_NAV_ITEMS,
}) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuth();
  const { openLoginDrawer } = useLoginDrawer();
  const pathname = location.pathname;

  const isCommunityUser = user?.role === 'community';

  if (shouldHideClientBottomNav(pathname, isCommunityUser)) {
    return null;
  }

  const isActive = (path: string) => {
    if (path === clientPaths.home) {
      return isClientHome(location.pathname);
    }
    if (path === clientPaths.profile.settings) {
      return location.pathname === clientPaths.profile.settings || location.pathname === `${clientPaths.profile.settings}/`;
    }
    return location.pathname.startsWith(path);
  };

  const scrollMainToTop = () => {
    document.querySelectorAll('main').forEach((container) => {
      container.scrollTo({ top: 0 });
    });
  };

  const handleTabClick = (item: NavItemConfig, active: boolean) => {
    impactLight();
    if (!isAuthenticated) {
      if (item.guestDrawer) {
        openLoginDrawer();
        return;
      }
      if (item.guestLoginPage) {
        navigate('/login', { replace: true });
        return;
      }
    }
    if (active) {
      scrollMainToTop();
      return;
    }
    navigate(item.path, { replace: true });
  };

  return (
    <nav className="fixed bottom-0 left-0 right-0 z-40 bg-white border-t border-gray-100 dark:border-gray-800 shadow-[0_-1px_0_rgba(0,0,0,0.05)] px-1.5 pt-1.5 pb-nav-safe md:hidden grid grid-cols-5 gap-1 select-none">
      {items.map((item) => {
        const active = isActive(item.path);

        return (
          <button
            key={item.path}
            onClick={() => handleTabClick(item, active)}
            type="button"
            className={`flex flex-col items-center justify-center gap-[3px] py-2 px-1 rounded-xl transition-all duration-150 cursor-pointer active:scale-95 active:opacity-80 ${
              active
                ? 'bg-[#FFF4EE] text-[#FC480D]'
                : 'bg-transparent text-gray-600 hover:text-gray-900 hover:bg-gray-50'
            }`}
          >
            <Icon
              icon={active ? item.activeIcon : item.icon}
              className={`w-6 h-6 transition-transform duration-150 ${
                active ? 'scale-105 text-[#FC480D]' : 'text-gray-600'
              }`}
            />
            <span
              className={`text-[10.5px] leading-tight transition-colors ${
                active ? 'font-semibold text-[#FC480D]' : 'font-medium text-gray-600'
              }`}
            >
              {item.label}
            </span>
          </button>
        );
      })}
    </nav>
  );
};

export default ClientMobileBottomNav;
